import { get } from 'lodash';
import simpleDebug from '@/helpers/simpleDebug';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import parseFieldKey, { ParsedField } from '@/components/Questionnaire/form/helpers/parseFieldKey';
import areValuesEqual from '@/components/Questionnaire/form/helpers/areValuesEqual';
import { FieldInputData } from './typings';
import getAgentFieldInputValues from '../getAgentFieldInputValues';

type Chunk = {
  numItems: number;
  fieldInputData: FieldInputData;
  fieldOutputs: ParsedField[];
};

export type ChunkSize = number | 'all';

const DEFAULT_CHUNK_SIZE = 5;

/*
 * Function to make sure that the chunkSize is valid
 */
const sanitizeChunkSize = (chunkSize: ChunkSize | undefined): ChunkSize => {
  if (chunkSize === 'all') {
    return 'all';
  }

  if (
    chunkSize === null
    || chunkSize === undefined
    || typeof chunkSize !== 'number'
  ) {
    return DEFAULT_CHUNK_SIZE;
  }

  return Math.max(1, chunkSize);
};

const pickChunkSizeForAgent = (aiAgent: { aiModel: string; chunkSize?: ChunkSize }): ChunkSize => {
  if (aiAgent.chunkSize !== undefined) {
    return aiAgent.chunkSize;
  }
  if (
    aiAgent.aiModel === 'boxIndex'
    || aiAgent.aiModel === 'omitForPdfFills'
  ) {
    return 'all';
  }
  return aiAgent.chunkSize ?? DEFAULT_CHUNK_SIZE;
};

export const pickChunkSize = (aiAgent: { aiModel: string; chunkSize?: ChunkSize }): ChunkSize => sanitizeChunkSize(pickChunkSizeForAgent(aiAgent));

class Chunks {
  chunks: Chunk[];

  private chunkSize: ChunkSize;

  private aiAgent: Pick<QuestionnaireApiModels.AiAgent, 'slug'>;

  private inputFieldKeys: Parameters<typeof getAgentFieldInputValues>[0]['inputFieldKeys'];

  private fieldsByKey: Parameters<typeof getAgentFieldInputValues>[0]['fieldsByKey'];

  private formValues: Record<string, string>;

  private parsedOutputFields: ParsedField[];

  private outputFieldKeysToBeRun: Set<string>;

  constructor({
    aiAgent,
    fieldsByKey,
    formValues,
    getResponseSource,
    inputFieldKeys,
    outputFieldKeys,
    size,
    allowManual,
  }: {
    aiAgent: Pick<QuestionnaireApiModels.AiAgent, 'slug'>;
    fieldsByKey: Parameters<typeof getAgentFieldInputValues>[0]['fieldsByKey'];
    formValues: Record<string, string>;
    getResponseSource: (fieldKey: string) => QuestionnaireApiModels.QuestionnaireResponse['source'];
    inputFieldKeys: Parameters<typeof getAgentFieldInputValues>[0]['inputFieldKeys'];
    outputFieldKeys: Parameters<typeof getAgentFieldInputValues>[0]['inputFieldKeys'];
    size: ChunkSize;
    allowManual?: boolean;
  }) {
    this.aiAgent = aiAgent;
    this.chunks = [];
    this.chunkSize = size;
    this.fieldsByKey = fieldsByKey;
    this.formValues = formValues;
    this.inputFieldKeys = inputFieldKeys;
    this.parsedOutputFields = outputFieldKeys
      .filter((outputKey) => allowManual || getResponseSource(outputKey) !== 'manual')
      .map((outputKey) => parseFieldKey(outputKey));
    this.outputFieldKeysToBeRun = new Set();
  }

  pickIndex(): number {
    if (this.chunkSize === 'all' || this.chunks.length === 0) {
      return 0;
    }
    const latestIndex = this.chunks.length - 1;
    if (this.chunks[latestIndex].numItems < this.chunkSize) {
      return latestIndex;
    }
    return latestIndex + 1;
  }

  addOutputField({
    outputField,
    fieldInputData,
  }: {
    outputField: ParsedField;
    fieldInputData: FieldInputData;
  }): void {
    if (this.outputFieldKeysToBeRun.has(outputField.fullFieldKey)) {
      return;
    }
    const index = this.pickIndex();
    if (!this.chunks[index]) {
      this.chunks[index] = {
        fieldInputData: {},
        fieldOutputs: [],
        numItems: 0,
      };
    }

    this.chunks[index].numItems += 1;
    this.chunks[index].fieldOutputs.push(outputField);
    this.chunks[index].fieldInputData = {
      ...this.chunks[index].fieldInputData,
      ...fieldInputData,
    };
    this.outputFieldKeysToBeRun.add(outputField.fullFieldKey);
  }

  /*
   * Find any output values and see if we can run the agent on those
   */
  addOutputFields({ unset }: { unset: boolean }): void {
    this.parsedOutputFields.forEach((outputField) => {
      const outputFieldKey = outputField.fullFieldKey;
      if (this.outputFieldKeysToBeRun.has(outputFieldKey)) {
        return;
      }
      const currentOutputFieldValue = get(this.formValues, outputFieldKey);
      if (unset ? currentOutputFieldValue === undefined || currentOutputFieldValue === null : true) {
        const { fieldInputData, hasAllValues } = getAgentFieldInputValues({
          fieldsByKey: this.fieldsByKey,
          formValues: this.formValues,
          inputFieldKeys: this.inputFieldKeys,
          rowsToRun: outputField.rowIndex !== null ? [outputField.rowIndex] : [],
        });

        simpleDebug(this.aiAgent.slug, `in unset output field loop`, hasAllValues, {
          fieldInputData,
          inputFieldKeys: this.inputFieldKeys,
          outputField,
        });

        if (hasAllValues) {
          this.addOutputField({ fieldInputData, outputField });
        }
      }
    });
  }

  handleWatchedFields({
    watchedFields,
    watchedFieldsPrevious,
  }: {
    watchedFields: Record<string, unknown>;
    watchedFieldsPrevious: Record<string, unknown>;
  }): void {
    Object.entries(watchedFields).forEach(([watchedFieldKey, watchedFieldValue]) => {
      const previousWatchedFieldValue = watchedFieldsPrevious[watchedFieldKey];
      const parsedInputField = parseFieldKey(watchedFieldKey);

      if (
        previousWatchedFieldValue === undefined
        || previousWatchedFieldValue === null
        || !areValuesEqual(previousWatchedFieldValue, watchedFieldValue)
      ) {
        const { fieldInputData, hasAllValues } = getAgentFieldInputValues({
          fieldsByKey: this.fieldsByKey,
          formValues: this.formValues,
          inputFieldKeys: this.inputFieldKeys,
          rowsToRun: parsedInputField.rowIndex !== null ? [parsedInputField.rowIndex] : [],
        });

        simpleDebug(this.aiAgent.slug, `in watch inputs loop`, hasAllValues, {
          fieldInputData,
          inputFieldKeys: this.inputFieldKeys,
          parsedInputField,
        });

        if (hasAllValues) {
          const theseOutputFieldsToGenerate = this.parsedOutputFields
            .filter(of => !this.outputFieldKeysToBeRun.has(of.fullFieldKey))
            .filter(of => (
              (
                of.rowIndex === parsedInputField.rowIndex
                && of.listFieldKey === parsedInputField.listFieldKey
              )
              || (
                parsedInputField.rowIndex === null
                && parsedInputField.listFieldKey === null
              )
            ));

          theseOutputFieldsToGenerate.forEach((outputField) => {
            this.addOutputField({
              fieldInputData,
              outputField,
            });
          });
        }
      }
    });
  }
}

export default Chunks;
